import React from 'react';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { prettyDate, prettyFloatMoney } from '@/helpers/utils';
import { DATE_FORMAT_DD_MMM_YYYY } from '@/helpers/common';
import { REPAYMENT_STATUSES } from '@/containers/RepaymentScheduleModule';
import { Preloader } from '@/components/Preloader';
import { EmptyContent } from '@/components/EmptyContent';
import { MyLink } from '@/components/MyLink';
import { APP_LINKS } from '@/helpers/links';
import { EMPTY_DASH } from '@/helpers/constants';
import { MyTooltip } from '@/webkit/MyTooltip';
import IconTooltip from './icon_tooltip.svg';
import IconShift from './icon_shift.svg';
import IconCollapse from './collaps.svg';
import s from './index.module.scss';

export const RepScheduleTable = ({ data, isLoading }) => {
  const location = useLocation();
  const [activeRows, setActiveRows] = useState([]);
  const { t } = useTranslation('repayment_schedule');
  const REPAYMENT_STATUSES_T = REPAYMENT_STATUSES(t);
  const mainPage = location.pathname === APP_LINKS.loansSchedule;
  const isEmpty = !data?.length;

  const onToggle = idx => {
    activeRows.includes(idx)
      ? setActiveRows(activeRows.filter(i => i !== idx))
      : setActiveRows([...activeRows, idx]);
  };

  return (
    <div className={s.table__wrapper}>
      <div className={s.table__inner}>
        <div className={s.table}>
          <div className={s.table__thead}>
            <div className={s.table__thead_col}>
              <div className={s.row}>
                <span>{t('due_date')}</span>
                {mainPage && (
                  <MyTooltip
                    theme="dark"
                    delay={500}
                    renderContent={() => (
                      <div
                        className={classnames(
                          'f-small-regular',
                          s.tooltip_content,
                        )}
                      >
                        <Trans
                          ns="repayment_schedule"
                          i18nKey="scheduled_payments_can_adjusted"
                          components={{
                            linkTerms: (
                              <MyLink
                                href={APP_LINKS.helpLoanTerms()}
                                isExternal
                                underline
                              />
                            ),
                          }}
                        />
                      </div>
                    )}
                    renderElement={() => (
                      <div className={s.tooltip_icon}>
                        <img
                          src={IconTooltip}
                          alt="tooltip"
                          width={16}
                          height={16}
                        />
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
            <div className={s.table__thead_col}>
              <span className={s.amount}>{t('amount')}</span>
            </div>
            <div className={s.table__thead_col}>{t('payment_received')}</div>
            <div className={s.table__thead_col}>{t('payment_date')}</div>
            <div className={s.table__thead_col}>{t('status')}</div>
          </div>

          {isLoading ? (
            <Preloader className={s.preloader} />
          ) : isEmpty ? (
            <div className={s.empty}>
              <EmptyContent
                title={t('no_repayments_yet')}
                text={t('repayment_will_appear')}
              />
            </div>
          ) : (
            <div className={s.table__tbody}>
              {data.map((item, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <div className={s.table__tbody_row}>
                      <div
                        className={classnames(
                          s.table__tbody_col,
                          REPAYMENT_STATUSES_T[item.status].className,
                        )}
                      >
                        <div className={s.row}>
                          <span className="f-small-accent">
                            {prettyDate(item.deadline, DATE_FORMAT_DD_MMM_YYYY)}
                          </span>
                          {item.flexibility_info && (
                            <MyTooltip
                              theme="dark"
                              renderContent={() => (
                                <div
                                  className={classnames(
                                    'f-small-regular',
                                    s.tooltip_content,
                                  )}
                                >
                                  {item.flexibility_info.earnings_amount < 0.01
                                    ? t('payment_was_moved_tooltip_zero', {
                                        brands:
                                          item.flexibility_info.brands.join(
                                            ', ',
                                          ),
                                      })
                                    : t('payment_was_moved_tooltip', {
                                        brands:
                                          item.flexibility_info.brands.join(
                                            ', ',
                                          ),
                                        amount: prettyFloatMoney(
                                          item.flexibility_info.earnings_amount,
                                          {
                                            decimalPlaces: 2,
                                          },
                                        ),
                                      })}
                                </div>
                              )}
                              renderElement={() => (
                                <div className={s.tooltip_icon}>
                                  <img
                                    src={IconShift}
                                    alt="tooltip"
                                    width={16}
                                    height={16}
                                  />
                                </div>
                              )}
                            />
                          )}
                        </div>
                      </div>
                      <div
                        className={classnames(
                          s.table__tbody_col,
                          REPAYMENT_STATUSES_T[item.status].className,
                        )}
                        onClick={() => onToggle(idx)}
                        style={{ width: '100%' }}
                      >
                        <div className={s.dropdown}>
                          <span className="f-small-regular">
                            {prettyFloatMoney(item.total_amount, {
                              decimalPlaces: 2,
                            })}
                          </span>
                          <img
                            src={IconCollapse}
                            alt="arrow"
                            width={20}
                            height={20}
                            style={{
                              cursor: 'pointer',
                              minWidth: '20px',
                              transform: `rotate(${activeRows.includes(idx) ? '180deg' : '0'})`,
                            }}
                          />
                        </div>
                      </div>
                      <div
                        className={classnames(
                          s.table__tbody_col,
                          'tl',
                          REPAYMENT_STATUSES_T[item.status].className,
                        )}
                      >
                        <span className="f-small-regular">
                          {item.payment_received
                            ? prettyFloatMoney(item.payment_received, {
                                decimalPlaces: 2,
                              })
                            : EMPTY_DASH}
                        </span>
                      </div>
                      <div
                        className={classnames(
                          s.table__tbody_col,
                          REPAYMENT_STATUSES_T[item.status].className,
                        )}
                      >
                        <span className="f-small-regular">
                          {item.payment_date
                            ? prettyDate(item.payment_date, DATE_FORMAT_DD_MMM_YYYY)
                            : EMPTY_DASH}
                        </span>
                      </div>
                      <div
                        className={classnames(
                          s.table__tbody_col,
                          'tl',
                          REPAYMENT_STATUSES_T[item.status].className,
                        )}
                      >
                        <span
                          className={classnames(
                            s.status,
                            item.status,
                            'f-small-accent',
                          )}
                        >
                          {REPAYMENT_STATUSES_T[item.status].label}
                        </span>
                      </div>
                    </div>

                    {activeRows.includes(idx) && (
                      <>
                        {item.interest > 0 && (
                          <div className={s.table__tbody_row}>
                            <div className={s.table__tbody_col}>
                              <span
                                className={classnames(
                                  'f-small-regular',
                                  s.subrow_label,
                                )}
                              >
                                {t('interest')}
                              </span>
                            </div>
                            <div
                              className={classnames(
                                s.table__tbody_col,
                                s.subrow_value,
                              )}
                            >
                              <span className="f-small-regular">
                                {prettyFloatMoney(item.interest, {
                                  decimalPlaces: 2,
                                })}
                              </span>
                            </div>
                          </div>
                        )}

                        {item.principal > 0 && (
                          <div className={s.table__tbody_row}>
                            <div className={s.table__tbody_col}>
                              <span
                                className={classnames(
                                  'f-small-regular',
                                  s.subrow_label,
                                )}
                              >
                                {t('principal')}
                              </span>
                            </div>
                            <div
                              className={classnames(
                                s.table__tbody_col,
                                s.subrow_value,
                              )}
                            >
                              <span className="f-small-regular">
                                {prettyFloatMoney(item.principal, {
                                  decimalPlaces: 2,
                                })}
                              </span>
                            </div>
                          </div>
                        )}

                        {item.success_fee > 0 && (
                          <div className={s.table__tbody_row}>
                            <div className={s.table__tbody_col}>
                              <span
                                className={classnames(
                                  'f-small-regular',
                                  s.subrow_label,
                                )}
                              >
                                {t('success_return')}
                              </span>
                            </div>
                            <div
                              className={classnames(
                                s.table__tbody_col,
                                s.subrow_value,
                              )}
                            >
                              <span className="f-small-regular">
                                {prettyFloatMoney(item.success_fee, {
                                  decimalPlaces: 2,
                                })}
                              </span>
                            </div>
                          </div>
                        )}

                        {item.flat_fee > 0 && (
                          <div className={s.table__tbody_row}>
                            <div className={s.table__tbody_col}>
                              <span
                                className={classnames(
                                  'f-small-regular',
                                  s.subrow_label,
                                )}
                              >
                                {t('flat_fee')}
                              </span>
                            </div>
                            <div
                              className={classnames(
                                s.table__tbody_col,
                                s.subrow_value,
                              )}
                            >
                              <span className="f-small-regular">
                                {prettyFloatMoney(item.flat_fee, {
                                  decimalPlaces: 2,
                                })}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};